import React, { useState, useEffect, FC, useRef } from "react";
import { Icon } from "../../components/Component";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Button } from "reactstrap";
import { Modal } from "antd";
import { convertNumberToWords } from "../../utils/Utils";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}
interface BuyerDetails {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstin: string;
  pan: string;
  cin: string;
}

interface POLines {
  sno: number;
  itemDescription: string;
  hsnSac: string;
  qty?: number;
  rate: number;
  cgstPercentage: number;
  cgstAmount: number;
  sgstPercentage: number;
  sgstAmount: number;
  taxableAmount: number;
  uom: string;
}

interface POTotals {
  taxableAmount: string;
  cgst: string;
  sgst: string;
  total: string;
}

interface POData {
  poNumber: string;
  poDate: string;
  terms: string;
  expectedShipmentDate: string;
  placeofSupply: string;
  creditDays: number;
  deliveryAddressString: string;
  lines: POLines[];
  totals: POTotals;
  contactPersonName: string;
  mobileNumber: string;
}

const PdfGenerator: FC<any> = ({
  buyerDetails,
  supplierDetails,
  poDetails,
  buyerOrgLogo,
  hasIGST,
  TermsAndConditions,
  getPdfUrl,
  poApprovals,
}: any) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);
  
  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);
  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(`${url}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            resolve(reader.result as string);
          } else {
            reject(new Error("Failed to read the image data"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Error reading the image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching the image:", error);
      throw new Error("Failed to fetch the image.");
    }
  };

  const drawBorderedBox = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Define the margins for the box (e.g., 10px margins)
    const margin = 5;
    const boxX = margin;
    const boxY = 20; // Start after the header
    const boxWidth = pageWidth - 2 * margin;
    const boxHeight = pageHeight - 41; // Leave space for the footer at the bottom

    // Draw the border box
    doc.setDrawColor(157, 157, 157);
    doc.setLineWidth(0.2);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  };
  const generatePDF = async (download?: boolean) => {
    const doc = new jsPDF();

    //const leftLogo = await fetchImageAsBase64('http://localhost:3000/static/media/logo-dark2x.8758ccf4e7a33281ce73.png');

    const addImageWithMaxHeight = async (
      doc: any,
      imageData: string,
      x: number,
      y: number,
      maxHeight: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = imageData;

        img.onload = function () {
          const aspectRatio = img.width / img.height;

          // Ensure that the image height is capped at maxHeight, adjusting width accordingly
          const height = Math.min(img.height, maxHeight); // Restrict height to maxHeight
          const width = height * aspectRatio; // Adjust width based on aspect ratio

          doc.addImage(img.src, "PNG", x, y, width, height); // Add image to the PDF
          resolve();
        };

        img.onerror = function (error) {
          console.error("Image failed to load", error);
          reject(new Error("Image load failed"));
        };
      });
    };

    const addHeader = async (doc: jsPDF, leftLogo: string | null) => {
      if (leftLogo) {
        // If the image fetch was successful, add the image
        await addImageWithMaxHeight(doc, leftLogo, 10, 5, 10); // Await to ensure it finishes before continuing
      } else {
        // If the image fetch failed, print fallback text
        doc.setFontSize(20);
        doc.text("Procurex", 10, 20); // Fallback text in case of image fetch failure
      }
    };
    const addTable = (doc: any, poDetails: any, currentY: any) => {
      const mainTitles: any = [
        { content: "Sno", rowSpan: 2 },
        { content: "Item & Description", rowSpan: 2 },
        { content: "HSN/SAC", rowSpan: 2 },
        { content: "QTY", rowSpan: 2 },
        { content: "Rate", rowSpan: 2 },
      ];
      if (hasIGST) {
        mainTitles.push({ content: "IGST", colSpan: 2 });
        mainTitles.push({ content: "Line Total", rowSpan: 2 });
      } else {
        mainTitles.push({ content: "CGST", colSpan: 2 });
        mainTitles.push({ content: "SGST", colSpan: 2 });
        mainTitles.push({ content: "Line Total", rowSpan: 2 });
      }
      const subTitles = [{ content: "%" }, { content: "Amount" }];
      if (!hasIGST) {
        subTitles.push({ content: "%" }, { content: "Amount" });
      }
      const head = [mainTitles, subTitles];

      const rows = poDetails?.lines?.map((line: any, index: any) => {
        const row = [
          (index + 1).toString(),
          line.itemDescription,
          line.hsnSac,
          line.qty || "",
          line.rate,
        ];

        if (hasIGST) {
          row.push(line.igstPercentage?.toFixed(2), line.igstAmount);
        } else {
          row.push(
            line.cgstPercentage?.toFixed(2),
            line.cgstAmount,
            line.sgstPercentage?.toFixed(2),
            line.sgstAmount
          );
        }

        row.push(line.taxableAmount);
        return row;
      });

      let footer: any = [];

      if (hasIGST) {
        footer = [
          [
            "Total In Words",
            "",
            "",
            "",
            "",
            { content: "Taxable Amount:", colSpan: 2 },
            poDetails?.totals?.taxableAmount,
          ],
          [
            {
              content:
                "Indian Rupee " +
                convertNumberToWords(
                  poDetails?.totals?.total?.replace(/,/g, "")
                ),
              colSpan: 4,
            },
            "",
            { content: "IGST:", colSpan: 2 },
            poDetails?.totals?.igst,
          ],
          [
            "",
            "",
            "",
            "",
            "",
            { content: "Total:", colSpan: 2 },
            poDetails?.totals?.total,
          ],
          ["", "", "", "", "", ""],
          [
            {
              content: "",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Authorised Signature", colSpan: 5, halign: "right" },
          ],
          [
            {
              content: "",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
        ];
      } else {
        footer = [
          [
            {
              content: "Total In Words",
              colSpan: 2,
              style: { halign: "left" },
            },
            "",
            "",
            "",
            "",
            { content: "Taxable Amount:", colSpan: 3 },
            poDetails?.totals?.taxableAmount,
          ],
          [
            {
              content:
                "Indian Rupee " +
                convertNumberToWords(
                  poDetails?.totals?.total?.replace(/,/g, "")
                ),
              colSpan: 5,
            },
            "",
            "",
            { content: "CGST :", colSpan: 2 },
            poDetails?.totals?.cgst,
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "SGST :", colSpan: 2 },
            poDetails?.totals?.sgst,
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Total:", colSpan: 2 },
            poDetails?.totals?.total,
          ],
          [
            {
              content: "",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            {
              content: "Authorised Signature  ",
              colSpan: 5,
              styles: { halign: "right" },
            },
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "", styles: { halign: "middle" } },
          ],
        ];
      }

      doc.autoTable({
        head,
        body: rows,
        foot: footer,
        startY: currentY,
        pageBreak: "auto",
        columnStyles: {
          4: { halign: "right" }, // Rate column width, right-aligned
          5: { halign: "right" }, // CGST % column width, right-aligned
          6: { halign: "right" }, // CGST Amt column width, right-aligned
          7: { halign: "right" }, // SGST % column width, right-aligned
          8: { halign: "right" }, // SGST Amt column width, right-aligned
          9: { halign: "right" }, // Taxable Amount column width, right-aligned
        },

        didParseCell: (data: any) => {
          // Make 'Total' row bold
          if (
            (data.section === "foot" && data.row.index === 3) ||
            (data.section === "foot" && data.row.index === 2 && hasIGST)
          ) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.halign = "right";
          }
          // Handle "Total In Words" text (first column of footer)
          else if (
            data.section === "foot" &&
            data.column.index === 0 &&
            (data.row.index === 0 || data.row.index === 1)
          ) {
            data.cell.styles.fontStyle = "bolditalic";
            data.cell.styles.halign = "left";
          }
          // Handle amount conversion text (spans multiple columns)
          else if (
            data.section === "foot" &&
            data.column.index === 0 &&
            (data.row.index === 1 || data.row.index === 2)
          ) {
            data.cell.styles.fontStyle = "bolditalic";
            data.cell.styles.halign = "left";
          }
          // Handle "Authorised Signature" row - keep it right-aligned
          else if (
            data.section === "foot" &&
            data.row.index >= (hasIGST ? 6 : 7) &&
            data.cell.text[0] &&
            data.cell.text[0].includes("Authorised Signature")
          ) {
            data.cell.styles.halign = "right";
          }
          // All other footer cells (including amounts) should be right-aligned
          else if (data.section === "foot") {
            data.cell.styles.fontStyle = "normal";
            data.cell.styles.halign = "right";
          }
          // Keep body cells right-aligned for amount columns
          else if (data.section === "body" && data.column.index >= 4) {
            data.cell.styles.halign = "right";
          }
        },
        didDrawCell: (data: any) => {
          const { cell } = data;

          doc.setDrawColor(150, 150, 150);

          if (
            data.section === "foot" &&
            (data.column.index === 6 ||
              data.column.index === 7 ||
              data.column.index === 8 ||
              data.column.index === 9 ||
              (data.column.index === 5 && hasIGST))
          ) {
            if (data.column.index === (hasIGST ? 5 : 6)) {
              doc.line(cell.x, cell.y, cell.x, cell.y + cell.height); // Left vertical line for the label column
            }

            if (data.row.index === 3) {
              doc.line(
                cell.x,
                cell.y + cell.height,
                cell.x + cell.width,
                cell.y + cell.height
              ); // Bottom horizontal line
            }
            if (data.row.index === (hasIGST ? 7 : 8)) {
              doc.line(
                cell.x,
                cell.y + cell.height,
                cell.x + cell.width,
                cell.y + cell.height
              ); // Bottom horizontal line
            }
          }
        },
        headStyles: {
          fontSize: 9,
          fillColor: [227, 227, 227],
          textColor: [31, 31, 31],
          valign: "middle",
        },
        footStyles: {
          fontSize: 9,
          textColor: [31, 31, 31],
          lineWidth: 0,
        },
        styles: {
          fillColor: [255, 255, 255],
          fontSize: 8,
          lineWidth: 0.2,
          cellPadding: 1.5,
          overflow: "linebreak",
        },

        theme: "grid",
        margin: { top: 20, bottom: 20, left: 5.1, right: 5.1 },
        showFoot: "lastPage",
      });
    };

    const breakLongText = (text: any, maxLineLength = 30) => {
      if (!text) return " ";
      return text.match(new RegExp(`.{1,${maxLineLength}}`, "g")).join("\n");
    };

    const addFooter = async (
      doc: jsPDF,
      pageNum: number,
      totalPages: number
    ) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(7);

      const line1 = `${buyerDetails.name}`;
      const line2 = `${buyerDetails.addressLine1}${buyerDetails.addressLine2}, ${buyerDetails.city} ${buyerDetails.pincode}, ${buyerDetails.state}, ${buyerDetails.country}`;
      const line3 = `CIN: ${buyerDetails.cin} `;
      const line4 = `GSTIN: ${buyerDetails.gstin} | PAN: ${buyerDetails.pan}`;

      const lastLineY = pageHeight - 7;
      const lineSpacing = 3;
      const startY = lastLineY - 3 * lineSpacing;

      // Add page number first (same position as PR - pageHeight - 10 from bottom)
      doc.setFontSize(8); // Set font size for page number
      const pageText = `Page ${pageNum} of ${totalPages}`;
      doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });

      // Add footer details centered (reset font size for footer)
      doc.setFontSize(7);
      doc.text(line1, pageWidth / 2, startY, { align: "center" });
      doc.text(line2, pageWidth / 2, startY + lineSpacing, { align: "center" });
      doc.text(line3, pageWidth / 2, startY + 2 * lineSpacing, {
        align: "center",
      });
      doc.text(line4, pageWidth / 2, startY + 3 * lineSpacing, {
        align: "center",
      });
    };
    const addGeneralDetailsSection = async (
      doc: jsPDF,
      buyerDetails: BuyerDetails,
      poDetails: POData,
      supplierDetails: any
    ) => {
      const sectionY = 16; // Starting Y position for the General Details Section
      const pageWidth = doc.internal.pageSize.getWidth();

      // Set font for heading and add text for General Details Section
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold"); // Bold for the heading
      doc.setTextColor(0, 0, 0); // Black text color

      let currentY = sectionY + 10; // Keep track of the current Y position

      // Add buyer details
      if (buyerDetails) {
        const lineHeight = 4;
        doc.text(buyerDetails.name || "", 10, currentY);
        currentY += 5; // Move Y position for the next line

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const address1 = breakLongText(buyerDetails.addressLine1 || "");
        doc.text(address1, 10, currentY);
        currentY += address1.split("\n").length * lineHeight;

        const address2 = breakLongText(buyerDetails.addressLine2 || "");
        doc.text(address2, 10, currentY);
        currentY += address2.split("\n").length * lineHeight;
        doc.text(
          `${buyerDetails.city || ""}, ${buyerDetails.state || ""},${
            buyerDetails.country || ""
          }, ${buyerDetails.pincode || ""}`,
          10,
          currentY
        );
        currentY += 5;
        const gstin = breakLongText(`GSTIN: ${buyerDetails.gstin || ""}`);
        doc.text(gstin, 10, currentY);
        currentY += gstin.split("\n").length * lineHeight;

        const pan = breakLongText(`PAN: ${buyerDetails.pan || ""}`);
        doc.text(pan, 10, currentY);
        currentY += pan.split("\n").length * lineHeight;

        const cin = breakLongText(`CIN: ${buyerDetails.cin || ""}`);
        doc.text(cin, 10, currentY);
        currentY += cin.split("\n").length * lineHeight;

        currentY += 5; // Add extra space after buyer details
      }

      // Add the "Purchase Order" text on the right, aligned at the top
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      const purchaseOrderText = "PURCHASE ORDER";

      // Calculate X position for the text to be right-aligned
      const textWidth = doc.getTextWidth(purchaseOrderText); // Get the width of the text
      const rightX = pageWidth - textWidth - 10; // Position the text with 10px padding from the right edge
      doc.setTextColor(20, 20, 20); // Black text color
      doc.text(purchaseOrderText, rightX, sectionY + 35); // Align the text at the top-right

      // Draw a horizontal line to separate sections
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(5, currentY, pageWidth - 5, currentY);
      currentY += 5;

      // Add Purchase Order details on the left and right sections
      const sectionWidth = (pageWidth - 10) / 2; // Subtracting the left and right margins
      let leftTableHeight = currentY;
      let rightTableHeight = currentY;

      // Capture the current Y position before the tables are drawn to draw the vertical line later
      const tableStartY = currentY;

      // Left table (PO details)
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: [
          ["Purchase Order Number:", breakLongText(poDetails.poNumber || " ")],
          ["Purchase Order Date:", poDetails.poDate || " "],
          ["Payment Terms:", poDetails.terms || " "],
          ["Credit Days:", poDetails?.creditDays?.toString() || " "],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the left table
        },
      });

      // Right table (Supplier details)
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: [
          ["Place of Supply:", poDetails?.placeofSupply || " "],
          ["Expected Shipment Date:", poDetails.expectedShipmentDate || " "],
          ["Delivery Address:", poDetails?.deliveryAddressString || " "],
          ["Contact Person:", poDetails?.contactPersonName || "N/A"],
          ["Mobile Number:", poDetails?.mobileNumber || "N/A"],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the right table
        },
      });

      const maxHeight = Math.max(leftTableHeight, rightTableHeight); // Calculate the maximum height

      // Draw the vertical line separator between the left and right tables without any gap on top and bottom
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(sectionWidth + 5, tableStartY, sectionWidth + 5, maxHeight + 5); // Ensure the line extends beyond the bottom by 5 units

      // Add Supplier Details section after the tables
      currentY = maxHeight + 10; // Move below the tables
      doc.setFillColor(200, 200, 200);
      doc.rect(5, currentY, pageWidth - 10, 6, "F"); // Title background
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Supplier Details", 10, currentY + 4);
      currentY += 12; // Move down after the title
      if (supplierDetails) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(supplierDetails.name || "", 10, currentY);
        currentY += 5;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        // if (
        //   ([supplierDetails.addressLine1, supplierDetails.addressLine2]?.filter(
        //     Boolean
        //   ) as any) > 0
        // ) {
        doc.text(
          [supplierDetails.addressLine1, supplierDetails.addressLine2]
            ?.filter(Boolean)
            ?.join(", "),
          10,
          currentY
        );
        currentY += 5;
        // }
        // if (
        //   ([
        //     supplierDetails?.city,
        //     supplierDetails?.state,
        //     supplierDetails?.country,
        //     supplierDetails?.pincode,
        //   ]?.filter(Boolean) as any) > 0
        // ) {
        doc.text(
          [
            supplierDetails?.city,
            supplierDetails?.state,
            supplierDetails?.country,
            supplierDetails?.pincode,
          ]
            ?.filter(Boolean)
            ?.join(", "),
          10,
          currentY
        );
        currentY += 5;
        // }
        doc.text(`CIN: ${supplierDetails.cin || ""}`, 10, currentY);
        currentY += 5;
        doc.text(`GSTIN: ${supplierDetails.gstin || ""}`, 10, currentY);
        currentY += 5;
        doc.text(`PAN: ${supplierDetails.pan || ""}`, 10, currentY);
        currentY += 10; // Add extra space after supplier details
      }

      return currentY; // Return the final Y position to calculate the next section start point
    };

    const addApprovalsSection = async (doc: jsPDF, approvals: any[]) => {
      if (!approvals || approvals.length === 0) {
        return;
      }

      // Add a new page for approvals
      doc.addPage();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Match the spacing from company name section
      // Border box starts at Y=20, company name starts at Y=26 (sectionY + 10 where sectionY=16)
      // So spacing from border to text is 6mm - apply same spacing for Approvals heading
      const borderY = 20; // Border box Y position (from drawBorderedBox)
      let currentY = borderY + 6; // Same spacing as company name (6mm from border)

      // Add title
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Approvals", pageWidth / 2, currentY, { align: "center" });

      // Add spacing between heading and table
      currentY += 8; // Reduced spacing between heading and table

      // Format date and time helper
      const formatDateTime = (dateString: string | null) => {
        if (!dateString) return "";
        try {
          // Handle date-only strings (e.g., "2025-12-18") by parsing components directly
          let date: Date;
          if (
            typeof dateString === "string" &&
            !dateString.includes("T") &&
            !dateString.includes(" ")
          ) {
            // Date-only string (YYYY-MM-DD), parse components to avoid timezone issues
            const parts = dateString.split("-");
            if (parts.length === 3) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
              const day = parseInt(parts[2], 10);
              date = new Date(year, month, day, 12, 0, 0); // Use local time noon
            } else {
              date = new Date(dateString);
            }
          } else {
            date = new Date(dateString);
          }

          // Check if date is valid
          if (isNaN(date.getTime())) return "";

          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          // If dateString doesn't include time, only return date
          if (
            typeof dateString === "string" &&
            !dateString.includes("T") &&
            !dateString.includes(" ")
          ) {
            return formattedDate;
          }

          const formattedTime = date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return `${formattedDate} ${formattedTime}`;
        } catch (error) {
          return "";
        }
      };

      // Prepare approval rows with signature data
      const approvalRowsData = await Promise.all(
        approvals.map(async (approval: any) => {
          let statusText = "";
          const status = (approval.status || "").toUpperCase();
          if (status === "PENDING") {
            const approvalDateTime = formatDateTime(approval.approvalDate);
            statusText = approvalDateTime
              ? `Pending\nDate: ${approvalDateTime}`
              : "Pending";
          } else if (status === "APPROVED") {
            const approvedDateTime = formatDateTime(approval.approvalDate);
            statusText = approvedDateTime
              ? `Approved\nDate: ${approvedDateTime}`
              : "Approved";
          } else if (status === "REJECTED") {
            const rejectedDateTime = formatDateTime(approval.approvalDate);
            statusText = rejectedDateTime
              ? `Rejected\nDate: ${rejectedDateTime}`
              : "Rejected";
          } else {
            statusText = approval.status || "";
          }

          // Fetch signature image if available
          // Only use signature from approval.dms field, don't match from root level dms array
          // dms can be an array or object, handle both cases
          let signatureImage: string | null = null;
          let signaturePath = null;

          if (Array.isArray(approval.dms) && approval.dms.length > 0) {
            // If dms is an array, get the first element's filePath
            signaturePath = approval.dms[0]?.filePath;
          } else if (approval.dms?.filePath) {
            // If dms is an object with filePath
            signaturePath = approval.dms.filePath;
          }

          if (signaturePath) {
            try {
              signatureImage = await fetchImageAsBase64(signaturePath);
            } catch (error) {
              console.error("Error fetching signature image:", error);
              signatureImage = null;
            }
          }

          return {
            name: approval.userName || "",
            role: approval.roleName || "",
            level: approval.level || approval.levelName || "",
            status: statusText,
            signature: signatureImage,
          };
        })
      );

      // Prepare table body (signature column will be handled in didDrawCell)
      const approvalRows = approvalRowsData.map((row: any) => [
        row.name,
        row.role,
        row.level,
        row.status,
        row.signature ? "" : "", // Empty string instead of "N/A"
      ]);

      // Calculate proper column widths based on page width
      const availableWidth = pageWidth - 20; // 10mm margin on each side

      // Add approvals table with signature column
      doc.autoTable({
        head: [["Name", "Role", "Level", "Status", "Signature"]],
        body: approvalRows,
        startY: currentY,
        margin: { top: 0, left: 10, right: 10, bottom: 10 },
        theme: "grid",
        headStyles: {
          fontSize: 10,
          fillColor: [227, 227, 227],
          textColor: [31, 31, 31],
          valign: "middle",
          halign: "center",
          cellPadding: 4,
          lineWidth: 0.5,
        },
        styles: {
          fontSize: 9,
          lineWidth: 0.2,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
        },
        columnStyles: {
          0: {
            cellWidth: availableWidth * 0.2,
            halign: "left",
            valign: "middle",
          },
          1: {
            cellWidth: availableWidth * 0.2,
            halign: "left",
            valign: "middle",
          },
          2: {
            cellWidth: availableWidth * 0.12,
            halign: "left",
            valign: "middle",
          },
          3: {
            cellWidth: availableWidth * 0.28,
            halign: "left",
            valign: "middle",
          },
          4: {
            cellWidth: availableWidth * 0.2,
            halign: "center",
            valign: "middle",
          },
        },
        didDrawCell: (data: any) => {
          // Draw signature images in the signature column (column index 4)
          if (data.column.index === 4 && data.section === "body") {
            const rowIndex = data.row.index;
            const signatureData = approvalRowsData[rowIndex];
            if (signatureData?.signature) {
              try {
                // Calculate image position within the cell (centered with proper padding)
                const cellPadding = 4;
                const imgWidth = Math.min(
                  35,
                  data.cell.width - cellPadding * 2
                );
                const imgHeight = Math.min(
                  15,
                  data.cell.height - cellPadding * 2
                );

                // Center the image within the cell, ensuring it stays within cell boundaries
                const x = data.cell.x + (data.cell.width - imgWidth) / 2;
                const y =
                  data.cell.y +
                  cellPadding +
                  (data.cell.height - cellPadding * 2 - imgHeight) / 2;

                doc.addImage(
                  signatureData.signature,
                  "PNG",
                  x,
                  y,
                  imgWidth,
                  imgHeight
                );
              } catch (error) {
                console.error("Error adding signature image:", error);
              }
            }
          }
        },
      });

      // Ensure the approvals page is properly set as the current page
      const totalPages = (doc as any).internal.getNumberOfPages();
      doc.setPage(totalPages); // Set to the approvals page (last page)

      // Note: Page numbers will be added by applyHeaderAndFooter, so we don't add them here
    };

    const addTermsAndConditions = (doc: jsPDF) => {
      // Set the font for Terms and Conditions
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Terms and Conditions", 10, 30);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const termsAndConditionsContent: any = TermsAndConditions || "";

      const maxLineWidth = 180; // Set the width to fit within the page margins
      const termsAndConditions = termsAndConditionsContent.split("\n");

      let currentY = 40;
      const lineHeight = 4.5;
      const pageHeight = doc.internal.pageSize.getHeight(); // Get the page height

      termsAndConditions.forEach((point: any) => {
        const wrappedText = doc.splitTextToSize(point, maxLineWidth);
        const requiredHeight = wrappedText.length * lineHeight;

        // Check if the required space exceeds the available space
        if (currentY + requiredHeight + 30 > pageHeight) {
          doc.addPage(); // Add a new page
          currentY = 30; // Reset Y position for the new page
        }

        doc.text(wrappedText, 10, currentY); // Print the text
        currentY += requiredHeight; // Adjust Y position
      });
    };

    // Generate PDF
    // const leftLogo = await fetchImageAsBase64(
    //   `https://procurexdevuatstg.blob.core.windows.net/procurexuatblob/prc8.png`
    // );
    // const leftLogo = await fetchImageAsBase64(buyerOrgLogo);
    let leftLogo: string | null = null;

    if (buyerOrgLogo) {
      try {
        leftLogo = await fetchImageAsBase64(buyerOrgLogo);
      } catch (error) {
        console.error("Buyer logo load failed", error);
        leftLogo = null;
      }
    }
    const currentY = await addGeneralDetailsSection(
      doc,
      buyerDetails,
      poDetails,
      supplierDetails
    );

    addTable(doc, poDetails, currentY);

    // Add approvals section if available
    if (poApprovals && poApprovals.length > 0) {
      await addApprovalsSection(doc, poApprovals);
    }

    const applyHeaderAndFooter = async () => {
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, leftLogo);
        await addFooter(doc, i, totalPages);
        drawBorderedBox(doc); // Ensure borders are added to each page if needed
      }
    };
    if (TermsAndConditions && TermsAndConditions.trim() !== "") {
      doc.addPage();
      addTermsAndConditions(doc);
    }
    await applyHeaderAndFooter();

    // Ensure all pages are properly finalized before saving/downloading
    const finalPageCount = (doc as any).internal.getNumberOfPages();

    // Make sure we're on the first page before saving to ensure all pages are included
    if (finalPageCount > 0) {
      doc.setPage(1);
    }

    // Finalize the PDF - ensure all pages are properly rendered
    // This ensures both preview and download have the same output
    for (let i = 1; i <= finalPageCount; i++) {
      doc.setPage(i);
      // Force page rendering by accessing internal properties
      (doc as any).internal.getPageInfo(i);
    }
    doc.setPage(1); // Return to first page

    if (download) {
      try {
        // Use output('blob') then save to ensure all pages are included
        const pdfBlob = doc.output("blob");
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = (poDetails.poNumber || "PO") + ".pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error during PDF download:", error);
        // Fallback: try using doc.save() directly
        try {
          doc.save((poDetails.poNumber || "PO") + ".pdf");
        } catch (saveError) {
          console.error("Error with doc.save():", saveError);
          alert("Failed to download PDF. Please try again.");
        }
      }
      return;
    }

    // Generate the PDF as a Blob
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    // setPdfUrl(pdfUrl); // Set the Blob URL to display the PDF inline
    return { pdfUrl, pdfBlob };
  };
  (async () => {
    try {
      const result = await generatePDF();
      if (result?.pdfBlob) {
        getPdfUrl(result.pdfBlob);
      }
    } catch (error) {
      console.log(error);
    }
  })();

  const downloadPDF = async () => {
    try {
      await generatePDF(true);
    } catch (error) {
      console.error("Error in downloadPDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  return (
    <>
      <Button
        type="button"
        color=""
        onClick={async () => {
          const { pdfUrl: _pdfUrl }: any = await generatePDF();
          setPdfUrl(_pdfUrl);
        }}
      >
        <Icon name="file-pdf" style={{ marginRight: 1 }} />
        <span>PDF</span>
      </Button>
      {pdfUrl && (
        <Modal
          open={pdfUrl !== null}
          onCancel={() => setPdfUrl(null)}
          width={"90vw"}
          footer={null}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Purchase Order</span>
              <span
                style={{ cursor: "pointer", userSelect: "none" }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadPDF().catch((error) => {
                    console.error("Download error:", error);
                  });
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadPDF().catch((error) => {
                    console.error("Download error:", error);
                  });
                }}
              >
                Download
                <Icon name="download" className="me-4" />
              </span>
            </div>
          }
        >
          <iframe
            src={pdfUrl + "#toolbar=0"}
            style={{
              width: "100%",
              height: "80vh",
            }}
            title="PDF Viewer"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default PdfGenerator;
